import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sequenceApi } from '../services/prospectingApi';
import type { SequenceCreate, SequenceUpdate, ListSequencesParams } from '../types/prospecting';

export const sequenceKeys = {
  all: ['sequences'] as const,
  list: (params?: ListSequencesParams) => [...sequenceKeys.all, 'list', params] as const,
  detail: (id: string) => [...sequenceKeys.all, 'detail', id] as const,
};

export function useSequences(params?: ListSequencesParams) {
  return useQuery({
    queryKey: sequenceKeys.list(params),
    queryFn: () => sequenceApi.list(params),
  });
}

export function useSequence(id: string) {
  return useQuery({
    queryKey: sequenceKeys.detail(id),
    queryFn: () => sequenceApi.get(id),
    enabled: !!id,
  });
}

export function useCreateSequence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SequenceCreate) => sequenceApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sequenceKeys.all });
    },
  });
}

export function useUpdateSequence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SequenceUpdate }) => sequenceApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: sequenceKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: sequenceKeys.all });
    },
  });
}

export function useDeleteSequence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => sequenceApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sequenceKeys.all });
    },
  });
}

export function useDuplicateSequence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => sequenceApi.duplicate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sequenceKeys.all });
    },
  });
}

export function useUpdateSequenceStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => sequenceApi.changeStatus(id, status),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: sequenceKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: sequenceKeys.all });
    },
  });
}
